import { forwardRef, useEffect, useState } from "react";
import { CardVariant } from "../../models/CardVariant";
import { classNames } from "../../style/common";
import { FIREBASE_STORAGE_URL } from "../../app-settings";
import { autoUpdate, flip, shift, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { Price } from "../../models/Price";
import { getTcgPlayerUrl } from "../../functions/Common";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/Tooltip/Tooltip";
import { CursorArrowRaysIcon } from "@heroicons/react/24/outline";

export function DeckCardImage(props: DeckCardImageProps) {
    if (!props.variant || !props.variant.id) {
        return <></>;
    }

    if (props.isHoverEnabled) {
        return <HoverDeckCardImage {...props} />
    }

    return <BaseDeckCardImage {...props} />
}

function HoverDeckCardImage(props: DeckCardImageProps) {
    const [isHoverImageOpen, setIsHoverImageOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open: isHoverImageOpen,
        onOpenChange: ((open) => {
            setIsHoverImageOpen(open);
        }),
        middleware: [shift({ padding: 10, crossAxis: true }), flip({ flipAlignment: true })],
        strategy: "fixed",
        whileElementsMounted: autoUpdate
    });

    const hover = useHover(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
    ]);

    return (
        <>
            <BaseDeckCardImage
                ref={refs.setReference}
                {...getReferenceProps()}
                {...props} />

            {
                (isHoverImageOpen) &&
                <div
                    ref={refs.setFloating}
                    style={{ ...floatingStyles, maxWidth: "300px", zIndex: 60, pointerEvents: 'none' }}
                    {...getFloatingProps({ className: "rounded-lg overflow-hidden" })}>
                    <div className="absolute bottom-[5px] left-[5px] bg-secondary rounded p-2 flex gap-1">
                        <span className="font-semibold">ctrl +</span>
                        <CursorArrowRaysIcon className="h-5 w-5 mt-[2px]" />
                    </div>
                    <img src={`${FIREBASE_STORAGE_URL}/images/cards/${props.variant.imageId}.jpg`} />
                </div>
            }
        </>
    )
}

const BaseDeckCardImage = forwardRef<HTMLDivElement, DeckCardImageProps>((props, ref) => {
    const [price, setPrice] = useState<Price | undefined>(undefined);

    const getNumberCollected = () => {
        if (!props.collection || !Object.keys(props.collection)?.length) {
            return 0;
        }

        const serialMatches = props.variantsBySerialDictionary[props.variant.serial];

        if (props.variant.altSerial) {
            const altSerialMatches = props.variantsBySerialDictionary[props.variant.altSerial] ?? [];

            for (const item of altSerialMatches) {
                if (serialMatches.find(x => x.id === item.id)) {
                    continue;
                }

                serialMatches.push(item);
            }
        }

        let collected = 0;
        for (const item of serialMatches) {
            const collectionCount = props.collection[item.id] ?? 0;
            collected += collectionCount;
        }

        return collected;
    }

    const [numberCollected, setNumberCollected] = useState(getNumberCollected());
    const [showCollectionCount, setShowCollectionCount] = useState(false);


    useEffect(() => {
        const newShowCollectionCount = props.isLoggedIn && !!props.integrateCollection;
        setShowCollectionCount(newShowCollectionCount);

        if (!newShowCollectionCount || !props.collection || !Object.keys(props.collection)?.length) {
            return;
        }

        setNumberCollected(getNumberCollected());
    }, [props.collection, props.integrateCollection, props.isLoggedIn]);

    useEffect(() => {
        if (!props.variant || !props.variant.id || !props.variantPriceDictionary) {
            setPrice(undefined);
            return;
        }

        setPrice(props.variantPriceDictionary[props.variant.id]);
    }, [props.variant, props.variantPriceDictionary]);

    return (
        <div className="relative flex flex-col gap-1 h-fit">
            {
                (showCollectionCount || !props.hideCardPrice) &&
                <div className="text-xs select-none leading-4 flex w-full bg-primary rounded shadow border border-color-border/25 items-center justify-between px-1">
                    {
                        showCollectionCount &&
                        <Tooltip>
                            <TooltipTrigger>
                                <div className={classNames("font-semibold", numberCollected >= props.count ? "text-green-600" : "text-contrast-tertiary")}>
                                    {numberCollected}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-full px-4" style={{ zIndex: 100 }}>
                                <div className="rounded-md bg-gray-900 border border-color-border/30 shadow p-4 text-sm text-white">
                                    <div>Quantity Collected</div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    }


                    {
                        !props.hideCardPrice &&
                        <Tooltip>
                            <TooltipTrigger>
                                <a href={getTcgPlayerUrl({ finish: props.variant.finish, tcgPlayerId: props.variant.tcgPlayerId })} target="_blank" className=" text-green-600 text-xs select-none leading-4">
                                    {price ? `$${price.price.toFixed(2)}` : "???"}
                                </a>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-full px-4" style={{ zIndex: 100 }}>
                                <div className="rounded-md bg-gray-900 border border-color-border/30 shadow p-4 text-sm text-white">
                                    <div>Estimated Price</div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    }
                </div>
            }
            <div
                onClick={(event) => {
                    if (event.ctrlKey) {
                        props.openImage(props.variant);
                        return;
                    }

                    if (props.isEditMode) {
                        if (props.isHoverEnabled) {
                            props.addCard(props.variant);
                        } else {
                            props.openImage(props.variant);
                        }
                    } else {
                        props.openImage(props.variant);
                    }
                }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (props.isEditMode) {
                        if (props.isHoverEnabled) {
                            props.removeCard(props.variant);
                        } else {
                            props.openImage(props.variant);
                        }
                    } else {
                        props.openImage(props.variant);
                    }
                }}
                className={classNames("relative", props.muteElements && "opacity-30")}
                style={{ height: `${140 + (15 * (props.count - 1))}px` }}
                ref={ref}
            >
                {
                    Array.from(Array(props.count).keys())
                        .map((index) => {
                            return <img
                                key={`${props.variant.id}-${index}`}
                                className={classNames(
                                    index === 0
                                        ? ""
                                        : `absolute`,
                                    "w-[100px] rounded"
                                )}
                                style={{
                                    top: `${index * 15}px`
                                }}
                                src={`${FIREBASE_STORAGE_URL}/images/cards/${props.variant.imageId}.jpg`} />
                        })
                }
            </div>
        </div>
    )
});

export type DeckCardImageProps = {
    removeCard: (variant: CardVariant) => void,
    addCard: (variant: CardVariant) => void,
    openImage: (variant: CardVariant) => void,
    variant: CardVariant,
    variantPriceDictionary: Record<string, Price> | undefined,
    hideCardPrice: boolean,
    collection: Record<string, number> | undefined,
    variantsBySerialDictionary: Record<string, CardVariant[]>,
    integrateCollection: boolean,
    isLoggedIn: boolean,
    isHoverEnabled: boolean,
    isEditMode: boolean,
    muteElements: boolean,
    count: number
};